import React, { useState, useEffect, useCallback } from 'react';
import { Header, StyledContainer } from './AppInfo.styled';
import axios from "axios";
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {env} from "../../../config/environment";

const AppInfo = () => {
    const [backendVersion, setBackendVersion] = useState('');
    const [frontendVersion, setFrontendVersion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const fetchVersionNumber = useCallback(async () => {
        setIsLoading(true);
        try {
            const backendVersionNumber = (await axios.get("/info/version")).data as string;
            setBackendVersion(backendVersionNumber);

            const frontendVersionNumber = env.version as string;
            setFrontendVersion(frontendVersionNumber);
        } catch (error) {
            showSnackbar(t('appInfo.errorMessage'), 'error');
        } finally {
            setIsLoading(false);
        }
    }, [ showSnackbar, t]);

    useEffect(() => {
        void fetchVersionNumber();
    }, [fetchVersionNumber]);

    return (
        <StyledContainer role="region" aria-labelledby="app-info-title">
            <Header>
                <Typography variant="h4" component="h2" id="app-info-title">{t('appInfo.title')}</Typography>
            </Header>

            <Box 
                sx={{ position: 'relative' }}
                aria-live="polite"
                aria-atomic={true}
                aria-busy={isLoading}
            >
                <Typography 
                    variant="body1" 
                    component="p"
                    aria-label={t('appInfo.backendVersion.ariaLabel', { version: backendVersion || '—' }) as string}
                >
                    {t('appInfo.backendVersion')} {backendVersion}
                </Typography>
                <Typography 
                    variant="body1" 
                    component="p"
                    aria-label={t('appInfo.frontendVersion.ariaLabel', { version: frontendVersion || '—' }) as string}
                >
                    {t('appInfo.frontendVersion')} {frontendVersion}
                </Typography>
            </Box>
            {isLoading && (
                <>
                    <CircularProgress
                        size={40}
                        aria-hidden={true}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-20px',
                            marginLeft: '-20px',
                        }}
                    />
                    <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                        {t('appInfo.loading')}
                    </Box>
                </>
            )}
        </StyledContainer>
    );
};

export default AppInfo;
